/**
 * CardDesignPreview
 *
 * Renders a premium 6.5x5 folded-card front (landscape, 1.3:1) in pure CSS + SVG.
 * Each style family has its own decorative artwork:
 *   floral      → botanical branch illustrations in all four corners
 *   elegant     → double-rule frame with corner flourishes
 *   modern      → geometric bracket corners
 *   sentimental → soft swash curves
 *   religious   → faith-specific sacred symbols (cross, menorah, star of david, crescent, rangoli)
 *   luxury      → art-deco triple frame with corner fans and centre medallion
 *   kids        → balloons, stars, confetti
 *
 * NO emoji anywhere on the card face — ever.
 */

import type { CSSProperties } from 'react';

export interface DesignData {
  id:            string;
  name:          string;
  heading:       string;
  tagline:       string;
  style:         string;
  bg_color:      string;
  bg_type:       'solid' | 'gradient';
  pattern:       string;
  pattern_color: string;
  accent_color:  string;
  text_color:    string;
  emoji:         string;
}

// ── Sizes — landscape 6.5x5 folded card (1.3:1, wider than tall) ──────────────

const SIZE_MAP = {
  xs: { w: 108, h: 83,  p: 5,  nameSize: 5.5, subSize: 3,   ruleW: 0.75 },
  sm: { w: 144, h: 111, p: 7,  nameSize: 7,   subSize: 4,   ruleW: 1    },
  md: { w: 216, h: 166, p: 11, nameSize: 9.5, subSize: 5.5, ruleW: 1    },
  lg: { w: 288, h: 222, p: 14, nameSize: 12,  subSize: 7,   ruleW: 1    },
};

export type DesignPreviewSize = keyof typeof SIZE_MAP;

// ── Pattern overlay ───────────────────────────────────────────────────────────

function patternStyle(pattern: string, color: string): CSSProperties {
  switch (pattern) {
    case 'dots':
      return { backgroundImage: `radial-gradient(circle, ${color} 1px, transparent 1px)`, backgroundSize: '14px 14px' };
    case 'grid':
      return { backgroundImage: `linear-gradient(${color} 1px, transparent 1px), linear-gradient(90deg, ${color} 1px, transparent 1px)`, backgroundSize: '18px 18px' };
    case 'diamonds':
      return { backgroundImage: `linear-gradient(45deg,${color} 25%,transparent 25%),linear-gradient(-45deg,${color} 25%,transparent 25%),linear-gradient(45deg,transparent 75%,${color} 75%),linear-gradient(-45deg,transparent 75%,${color} 75%)`, backgroundSize: '10px 10px', backgroundPosition: '0 0,0 5px,5px -5px,-5px 0px' };
    case 'botanical':
      return { backgroundImage: `repeating-linear-gradient(30deg,${color},${color} 1px,transparent 1px,transparent 14px),repeating-linear-gradient(-30deg,${color},${color} 1px,transparent 1px,transparent 14px)` };
    case 'waves':
      return { backgroundImage: `repeating-radial-gradient(ellipse farthest-corner at 0% 50%,transparent 0,transparent 9px,${color} 9px,${color} 10px,transparent 10px,transparent 20px)` };
    case 'confetti':
      return { backgroundImage: `repeating-linear-gradient(45deg,${color},${color} 1px,transparent 1px,transparent 12px),repeating-linear-gradient(-45deg,${color},${color} 1px,transparent 1px,transparent 16px)` };
    default:
      return {};
  }
}

// ── Style-specific SVG decorations (landscape-optimised) ──────────────────────

/** Floral botanical branches in all four corners — landscape spread */
function FloralDecoration({ color, w, h }: { color: string; w: number; h: number }) {
  const s = Math.min(w, h) * 0.42;
  return (
    <svg
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      viewBox={`0 0 ${w} ${h}`}
      fill="none"
      aria-hidden
    >
      {/* Top-left botanical */}
      <g opacity="0.28" transform="translate(2,2)">
        <path d={`M0 ${s*0.9} Q${s*0.1} ${s*0.3} ${s*0.55} 0`} stroke={color} strokeWidth="0.8" />
        <ellipse cx={s*0.18} cy={s*0.55} rx={s*0.12} ry={s*0.07} fill={color} transform={`rotate(-40,${s*0.18},${s*0.55})`} />
        <ellipse cx={s*0.35} cy={s*0.32} rx={s*0.12} ry={s*0.07} fill={color} transform={`rotate(-60,${s*0.35},${s*0.32})`} />
        <ellipse cx={s*0.48} cy={s*0.14} rx={s*0.09} ry={s*0.05} fill={color} transform={`rotate(-75,${s*0.48},${s*0.14})`} />
        <circle cx={s*0.56} cy={s*0.03} r={s*0.04} fill={color} />
        <circle cx={s*0.1}  cy={s*0.78} r={s*0.03} fill={color} />
      </g>
      {/* Top-right botanical (mirrored) */}
      <g opacity="0.28" transform={`translate(${w-2},2) scale(-1,1)`}>
        <path d={`M0 ${s*0.9} Q${s*0.1} ${s*0.3} ${s*0.55} 0`} stroke={color} strokeWidth="0.8" />
        <ellipse cx={s*0.18} cy={s*0.55} rx={s*0.12} ry={s*0.07} fill={color} transform={`rotate(-40,${s*0.18},${s*0.55})`} />
        <ellipse cx={s*0.35} cy={s*0.32} rx={s*0.12} ry={s*0.07} fill={color} transform={`rotate(-60,${s*0.35},${s*0.32})`} />
        <ellipse cx={s*0.48} cy={s*0.14} rx={s*0.09} ry={s*0.05} fill={color} transform={`rotate(-75,${s*0.48},${s*0.14})`} />
        <circle cx={s*0.56} cy={s*0.03} r={s*0.04} fill={color} />
      </g>
      {/* Bottom-left (flipped vertically) */}
      <g opacity="0.22" transform={`translate(2,${h-2}) scale(1,-1)`}>
        <path d={`M0 ${s*0.9} Q${s*0.1} ${s*0.3} ${s*0.55} 0`} stroke={color} strokeWidth="0.8" />
        <ellipse cx={s*0.18} cy={s*0.55} rx={s*0.12} ry={s*0.07} fill={color} transform={`rotate(-40,${s*0.18},${s*0.55})`} />
        <ellipse cx={s*0.35} cy={s*0.32} rx={s*0.12} ry={s*0.07} fill={color} transform={`rotate(-60,${s*0.35},${s*0.32})`} />
        <circle cx={s*0.56} cy={s*0.03} r={s*0.04} fill={color} />
      </g>
      {/* Bottom-right */}
      <g opacity="0.22" transform={`translate(${w-2},${h-2}) scale(-1,-1)`}>
        <path d={`M0 ${s*0.9} Q${s*0.1} ${s*0.3} ${s*0.55} 0`} stroke={color} strokeWidth="0.8" />
        <ellipse cx={s*0.18} cy={s*0.55} rx={s*0.12} ry={s*0.07} fill={color} transform={`rotate(-40,${s*0.18},${s*0.55})`} />
        <ellipse cx={s*0.35} cy={s*0.32} rx={s*0.12} ry={s*0.07} fill={color} transform={`rotate(-60,${s*0.35},${s*0.32})`} />
        <circle cx={s*0.56} cy={s*0.03} r={s*0.04} fill={color} />
      </g>
      {/* Centre small blossom — positioned in lower half so it shows below text */}
      {[0,60,120,180,240,300].map((deg, i) => (
        <ellipse
          key={i}
          cx={w/2} cy={h*0.66}
          rx={Math.min(w,h)*0.048} ry={Math.min(w,h)*0.02}
          fill={color} opacity="0.18"
          transform={`rotate(${deg},${w/2},${h*0.66})`}
        />
      ))}
      <circle cx={w/2} cy={h*0.66} r={Math.min(w,h)*0.024} fill={color} opacity="0.25" />
    </svg>
  );
}

/** Elegant double-rule frame with corner flourishes */
function ElegantDecoration({ color, w, h, p }: { color: string; w: number; h: number; p: number }) {
  const inset = p * 0.65;
  const cr    = p * 0.55;
  return (
    <svg
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      viewBox={`0 0 ${w} ${h}`}
      fill="none"
      aria-hidden
    >
      <rect x={inset*0.5} y={inset*0.5} width={w-inset} height={h-inset}
        stroke={color} strokeWidth="0.5" opacity="0.35" />
      <rect x={inset} y={inset} width={w-inset*2} height={h-inset*2}
        stroke={color} strokeWidth="0.5" opacity="0.5" />
      {[
        [inset, inset, 1, 1],
        [w-inset, inset, -1, 1],
        [inset, h-inset, 1, -1],
        [w-inset, h-inset, -1, -1],
      ].map(([cx, cy, sx, sy], i) => (
        <g key={i} opacity="0.6">
          <line x1={cx} y1={cy} x2={cx + sx*cr*1.4} y2={cy} stroke={color} strokeWidth="1" />
          <line x1={cx} y1={cy} x2={cx} y2={cy + sy*cr*1.4} stroke={color} strokeWidth="1" />
          <circle cx={cx} cy={cy} r={1.2} fill={color} />
        </g>
      ))}
      {/* Left and right ornament diamonds — lower half */}
      <polygon points={`${inset*0.5},${h*0.66-3} ${inset*0.5+3},${h*0.66} ${inset*0.5},${h*0.66+3} ${inset*0.5-3},${h*0.66}`}
        fill={color} opacity="0.45" />
      <polygon points={`${w-inset*0.5},${h*0.66-3} ${w-inset*0.5+3},${h*0.66} ${w-inset*0.5},${h*0.66+3} ${w-inset*0.5-3},${h*0.66}`}
        fill={color} opacity="0.45" />
    </svg>
  );
}

/** Modern geometric bracket corners */
function ModernDecoration({ color, w, h, p }: { color: string; w: number; h: number; p: number }) {
  const arm   = p * 1.4;
  const thick = 1.2;
  const corners = [
    [p*0.4, p*0.4, 1, 1],
    [w-p*0.4, p*0.4, -1, 1],
    [p*0.4, h-p*0.4, 1, -1],
    [w-p*0.4, h-p*0.4, -1, -1],
  ];
  return (
    <svg
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      viewBox={`0 0 ${w} ${h}`}
      fill="none"
      aria-hidden
    >
      {corners.map(([cx, cy, sx, sy], i) => (
        <g key={i} opacity="0.55">
          <line x1={cx} y1={cy} x2={cx + sx*arm} y2={cy} stroke={color} strokeWidth={thick} strokeLinecap="square" />
          <line x1={cx} y1={cy} x2={cx} y2={cy + sy*arm} stroke={color} strokeWidth={thick} strokeLinecap="square" />
        </g>
      ))}
      {/* Horizontal centre accent — in lower half so it shows below text */}
      <line x1={w*0.35} y1={h*0.67} x2={w*0.65} y2={h*0.67} stroke={color} strokeWidth="0.5" opacity="0.18" />
    </svg>
  );
}

/** Sentimental — soft curved swash borders, landscape-tuned */
function SentimentalDecoration({ color, w, h, p }: { color: string; w: number; h: number; p: number }) {
  return (
    <svg
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      viewBox={`0 0 ${w} ${h}`}
      fill="none"
      aria-hidden
    >
      {/* Sweeping top swash */}
      <path
        d={`M ${p*0.5} ${p*1.1} Q ${w*0.25} ${p*0.3} ${w/2} ${p*0.75} Q ${w*0.75} ${p*1.2} ${w-p*0.5} ${p*0.85}`}
        stroke={color} strokeWidth="0.7" opacity="0.3" />
      {/* Sweeping bottom swash */}
      <path
        d={`M ${p*0.5} ${h-p*1.1} Q ${w*0.25} ${h-p*0.3} ${w/2} ${h-p*0.75} Q ${w*0.75} ${h-p*1.2} ${w-p*0.5} ${h-p*0.85}`}
        stroke={color} strokeWidth="0.7" opacity="0.3" />
      {/* Left and right vertical swashes — shorter for landscape */}
      <path d={`M ${p*0.6} ${h*0.3} Q ${p*0.2} ${h/2} ${p*0.6} ${h*0.7}`}
        stroke={color} strokeWidth="0.5" opacity="0.2" />
      <path d={`M ${w-p*0.6} ${h*0.3} Q ${w-p*0.2} ${h/2} ${w-p*0.6} ${h*0.7}`}
        stroke={color} strokeWidth="0.5" opacity="0.2" />
      {[[p*0.7,p*0.7],[w-p*0.7,p*0.7],[p*0.7,h-p*0.7],[w-p*0.7,h-p*0.7]].map(([cx,cy],i) => (
        <circle key={i} cx={cx} cy={cy} r={1.2} fill={color} opacity="0.35" />
      ))}
    </svg>
  );
}

/** Religious — distinct sacred symbols per faith tradition, positioned as elegant watermarks */
function ReligiousDecoration({ color, w, h, p, designId = '' }: {
  color: string; w: number; h: number; p: number; designId?: string;
}) {
  const cx = w / 2;
  const cy = h * 0.64;  // lower half, symbols show below text
  const r  = Math.min(w, h) * 0.30;

  // Corner hairlines shared by all religious designs
  const corners = (
    <>
      <line x1={p*0.5}   y1={p*0.5}   x2={p*2.8}   y2={p*0.5}   stroke={color} strokeWidth="0.5" opacity="0.35" />
      <line x1={p*0.5}   y1={p*0.5}   x2={p*0.5}   y2={p*2.8}   stroke={color} strokeWidth="0.5" opacity="0.35" />
      <line x1={w-p*0.5} y1={p*0.5}   x2={w-p*2.8} y2={p*0.5}   stroke={color} strokeWidth="0.5" opacity="0.35" />
      <line x1={w-p*0.5} y1={p*0.5}   x2={w-p*0.5} y2={p*2.8}   stroke={color} strokeWidth="0.5" opacity="0.35" />
      <line x1={p*0.5}   y1={h-p*0.5} x2={p*2.8}   y2={h-p*0.5} stroke={color} strokeWidth="0.5" opacity="0.35" />
      <line x1={p*0.5}   y1={h-p*0.5} x2={p*0.5}   y2={h-p*2.8} stroke={color} strokeWidth="0.5" opacity="0.35" />
      <line x1={w-p*0.5} y1={h-p*0.5} x2={w-p*2.8} y2={h-p*0.5} stroke={color} strokeWidth="0.5" opacity="0.35" />
      <line x1={w-p*0.5} y1={h-p*0.5} x2={w-p*0.5} y2={h-p*2.8} stroke={color} strokeWidth="0.5" opacity="0.35" />
    </>
  );

  // ── Menorah (Hanukkah) ─────────────────────────────────────────────────────
  if (designId.includes('hanukkah')) {
    const mW = r * 1.1;
    const mH = r * 0.85;
    const positions = [-4, -3, -2, -1, 0, 1, 2, 3, 4];
    const spacing   = mW * 0.115;
    return (
      <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none' }}
        viewBox={`0 0 ${w} ${h}`} fill="none" aria-hidden>
        {corners}
        <g opacity="0.14" transform={`translate(${cx},${cy})`}>
          {/* Base */}
          <rect x={-mW*0.5} y={mH*0.32} width={mW} height={mH*0.09} fill={color} rx="1" />
          {/* Center stem */}
          <rect x={-mW*0.022} y={-mH*0.22} width={mW*0.044} height={mH*0.54} fill={color} />
          {/* Outer branches */}
          {[-4,-3,-2,-1,1,2,3,4].map((pos) => {
            const x  = pos * spacing;
            const ah = Math.abs(pos) * mH * 0.065;
            return (
              <g key={pos}>
                <path d={`M 0 ${-mH*0.2} Q ${x*0.5} ${-mH*0.2-ah} ${x} ${-mH*0.22}`}
                  stroke={color} strokeWidth={mW*0.03} fill="none" />
                <rect x={x-mW*0.022} y={-mH*0.46} width={mW*0.044} height={mH*0.24} fill={color} />
              </g>
            );
          })}
          {/* Shamash (center candle, slightly taller) */}
          <rect x={-mW*0.022} y={-mH*0.56} width={mW*0.044} height={mH*0.34} fill={color} />
          {/* Flames */}
          {positions.map((pos) => {
            const x    = pos * spacing;
            const topY = pos === 0 ? -mH*0.58 : -mH*0.48;
            return (
              <ellipse key={pos} cx={x} cy={topY - mH*0.045}
                rx={mW*0.024} ry={mW*0.036} fill={color} opacity="0.65" />
            );
          })}
        </g>
      </svg>
    );
  }

  // ── Star of David (Jewish) ─────────────────────────────────────────────────
  if (designId.includes('star-david')) {
    const starR = r * 0.52;
    const tri1  = [0, 1, 2].map(i => {
      const a = (i / 3) * Math.PI * 2 - Math.PI / 2;
      return `${cx + Math.cos(a)*starR},${cy + Math.sin(a)*starR}`;
    }).join(' ');
    const tri2  = [0, 1, 2].map(i => {
      const a = (i / 3) * Math.PI * 2 + Math.PI / 2;
      return `${cx + Math.cos(a)*starR},${cy + Math.sin(a)*starR}`;
    }).join(' ');
    return (
      <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none' }}
        viewBox={`0 0 ${w} ${h}`} fill="none" aria-hidden>
        {corners}
        <polygon points={tri1} stroke={color} strokeWidth="1.2" fill={color} opacity="0.10" />
        <polygon points={tri2} stroke={color} strokeWidth="1.2" fill={color} opacity="0.10" />
        {/* Small center hex */}
        <circle cx={cx} cy={cy} r={starR*0.2} fill={color} opacity="0.12" />
      </svg>
    );
  }

  // ── Crescent & Geometric Star (Islamic) ───────────────────────────────────
  if (designId.includes('eid') || designId.includes('ramadan')) {
    const outerR = r * 0.44;
    const dx     = outerR * 0.36;
    const innerR = outerR * 0.82;
    const crescentPath = [
      `M ${cx} ${cy - outerR}`,
      `A ${outerR} ${outerR} 0 1 1 ${cx} ${cy + outerR}`,
      `A ${outerR} ${outerR} 0 1 1 ${cx} ${cy - outerR}`,
      `M ${cx + dx} ${cy - innerR}`,
      `A ${innerR} ${innerR} 0 1 0 ${cx + dx} ${cy + innerR}`,
      `A ${innerR} ${innerR} 0 1 0 ${cx + dx} ${cy - innerR}`,
    ].join(' ');
    // 5-point star to the right of crescent
    const starCx = cx + outerR * 0.92;
    const starCy = cy - outerR * 0.38;
    const sr     = outerR * 0.22;
    const sir    = sr * 0.42;
    const starPts = Array.from({ length: 10 }).map((_, i) => {
      const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
      const rad = i % 2 === 0 ? sr : sir;
      return `${starCx + Math.cos(a)*rad},${starCy + Math.sin(a)*rad}`;
    }).join(' ');
    return (
      <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none' }}
        viewBox={`0 0 ${w} ${h}`} fill="none" aria-hidden>
        {corners}
        {/* 8-pt geometric border rings at corners */}
        <path d={crescentPath} fill={color} fillRule="evenodd" opacity="0.12" />
        <polygon points={starPts} fill={color} opacity="0.18" />
      </svg>
    );
  }

  // ── Rangoli / Diya (Diwali) ────────────────────────────────────────────────
  if (designId.includes('diwali')) {
    return (
      <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none' }}
        viewBox={`0 0 ${w} ${h}`} fill="none" aria-hidden>
        {corners}
        {/* Rangoli geometric rings */}
        <circle cx={cx} cy={cy} r={r*0.48} stroke={color} strokeWidth="0.5" fill="none" opacity="0.12" />
        <circle cx={cx} cy={cy} r={r*0.28} stroke={color} strokeWidth="0.5" fill="none" opacity="0.10" />
        {/* Petal ring */}
        {[0,45,90,135,180,225,270,315].map((deg, i) => {
          const a  = deg * Math.PI / 180;
          const ex = cx + Math.cos(a) * r * 0.38;
          const ey = cy + Math.sin(a) * r * 0.38;
          return (
            <ellipse key={i} cx={ex} cy={ey} rx={r*0.10} ry={r*0.055}
              fill={color} opacity="0.11"
              transform={`rotate(${deg},${ex},${ey})`} />
          );
        })}
        {/* Inner spokes */}
        {[0,60,120,180,240,300].map((deg, i) => {
          const a = deg * Math.PI / 180;
          return (
            <line key={i}
              x1={cx + Math.cos(a)*r*0.1} y1={cy + Math.sin(a)*r*0.1}
              x2={cx + Math.cos(a)*r*0.26} y2={cy + Math.sin(a)*r*0.26}
              stroke={color} strokeWidth="0.7" opacity="0.14" />
          );
        })}
      </svg>
    );
  }

  // ── Default: Christian Cross (watermark) ──────────────────────────────────
  const crossVH    = r * 0.80;   // vertical half-height
  const crossHW    = r * 0.52;   // horizontal half-width
  const crossThick = r * 0.095;  // bar thickness
  const barY       = cy - r * 0.26; // crossbar sits upper-third

  return (
    <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none' }}
      viewBox={`0 0 ${w} ${h}`} fill="none" aria-hidden>
      {/* Radiant light rays — very subtle */}
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i / 8) * Math.PI * 2;
        return (
          <line key={i}
            x1={cx + Math.cos(a)*r*0.06} y1={cy + Math.sin(a)*r*0.06}
            x2={cx + Math.cos(a)*r*0.85} y2={cy + Math.sin(a)*r*0.85}
            stroke={color} strokeWidth="0.4" opacity="0.07" />
        );
      })}
      {/* Cross — large watermark, very transparent */}
      <rect x={cx-crossThick} y={cy-crossVH}
        width={crossThick*2} height={crossVH*2}
        fill={color} opacity="0.09" rx="1" />
      <rect x={cx-crossHW} y={barY-crossThick*0.75}
        width={crossHW*2} height={crossThick*1.5}
        fill={color} opacity="0.09" rx="1" />
      {corners}
    </svg>
  );
}

/** Kids — balloons, stars, scattered confetti — landscape spread */
function KidsDecoration({ color, w, h, p }: { color: string; w: number; h: number; p: number }) {
  const s = Math.min(w, h);
  return (
    <svg
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      viewBox={`0 0 ${w} ${h}`}
      fill="none"
      aria-hidden
    >
      {/* Balloons — spread across wider card */}
      <ellipse cx={p*1.6} cy={p*1.6} rx={s*0.08} ry={s*0.1} fill={color} opacity="0.35" />
      <line x1={p*1.6} y1={p*1.6+s*0.1} x2={p*1.4} y2={p*3.0} stroke={color} strokeWidth="0.6" opacity="0.3" />
      <ellipse cx={w-p*1.6} cy={p*1.4} rx={s*0.07} ry={s*0.09} fill={color} opacity="0.28" />
      <line x1={w-p*1.6} y1={p*1.4+s*0.09} x2={w-p*1.8} y2={p*2.8} stroke={color} strokeWidth="0.6" opacity="0.25" />
      <ellipse cx={w*0.5} cy={p*1.2} rx={s*0.06} ry={s*0.08} fill={color} opacity="0.22" />
      <line x1={w*0.5} y1={p*1.2+s*0.08} x2={w*0.49} y2={p*2.5} stroke={color} strokeWidth="0.5" opacity="0.2" />
      {/* Stars scattered across wider format */}
      {[
        [w*0.82, h*0.22, 2.2],
        [w*0.12, h*0.45, 1.8],
        [w*0.92, h*0.62, 1.6],
        [w*0.18, h*0.75, 2.0],
        [w*0.68, h*0.82, 1.5],
        [w*0.45, h*0.15, 1.7],
      ].map(([cx, cy, r], i) => (
        <g key={i} opacity="0.4">
          <circle cx={cx} cy={cy} r={r*0.5} fill={color} />
          <line x1={cx - r} y1={cy} x2={cx + r} y2={cy} stroke={color} strokeWidth="0.8" />
          <line x1={cx} y1={cy - r} x2={cx} y2={cy + r} stroke={color} strokeWidth="0.8" />
        </g>
      ))}
      {/* Confetti dots — more spread for wider card */}
      {[
        [w*0.6, h*0.1], [w*0.35, h*0.08], [w*0.78, h*0.38],
        [w*0.08, h*0.55], [w*0.52, h*0.9], [w*0.88, h*0.8],
        [w*0.25, h*0.18], [w*0.72, h*0.7],
      ].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r={1.3} fill={color} opacity="0.35" />
      ))}
      {/* Bottom wavy swoosh — spans wider card */}
      <path d={`M ${p*0.5} ${h-p*0.7} Q ${w*0.25} ${h-p*1.5} ${w*0.5} ${h-p*0.8} Q ${w*0.75} ${h-p*0.3} ${w-p*0.5} ${h-p*0.9}`}
        stroke={color} strokeWidth="0.7" opacity="0.2" />
    </svg>
  );
}

/** Luxury — precise triple-ruled frame with art-deco corner fans and centre medallion */
function LuxuryDecoration({ color, w, h, p }: { color: string; w: number; h: number; p: number }) {
  const f1  = p * 0.35;
  const f2  = p * 0.80;
  const f3  = p * 1.30;
  const arm = p * 2.4;
  return (
    <svg
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      viewBox={`0 0 ${w} ${h}`}
      fill="none"
      aria-hidden
    >
      {/* Triple frame */}
      <rect x={f1} y={f1} width={w-f1*2} height={h-f1*2} stroke={color} strokeWidth="0.3" opacity="0.22" />
      <rect x={f2} y={f2} width={w-f2*2} height={h-f2*2} stroke={color} strokeWidth="0.7" opacity="0.42" />
      <rect x={f3} y={f3} width={w-f3*2} height={h-f3*2} stroke={color} strokeWidth="0.3" opacity="0.18" />

      {/* Art-deco corner fans */}
      {([
        [f2, f2, 1, 1],
        [w-f2, f2, -1, 1],
        [f2, h-f2, 1, -1],
        [w-f2, h-f2, -1, -1],
      ] as [number,number,number,number][]).map(([cx, cy, sx, sy], i) => (
        <g key={i}>
          {[0, 0.2, 0.4, 0.6, 0.8, 1].map((t, j) => (
            <line key={j}
              x1={cx} y1={cy}
              x2={cx + sx*arm*t} y2={cy + sy*arm*(1-t)}
              stroke={color} strokeWidth="0.45"
              opacity={0.42 - j * 0.06} />
          ))}
          <circle cx={cx} cy={cy} r={1.6} fill={color} opacity="0.55" />
        </g>
      ))}

      {/* Medallion — in lower half so it shows clearly below text */}
      <circle cx={w/2} cy={h*0.65} r={p*1.9}  stroke={color} strokeWidth="0.4" opacity="0.11" />
      <circle cx={w/2} cy={h*0.65} r={p*1.45} stroke={color} strokeWidth="0.25" opacity="0.09" />

      {/* Diamond */}
      <polygon
        points={`${w/2},${h*0.65-4.5} ${w/2+4.5},${h*0.65} ${w/2},${h*0.65+4.5} ${w/2-4.5},${h*0.65}`}
        fill={color} opacity="0.22" />

      {/* Horizontal hairlines flanking medallion */}
      <line x1={f3+p*0.6} y1={h*0.65} x2={w/2-p*2.8} y2={h*0.65}
        stroke={color} strokeWidth="0.35" opacity="0.14" />
      <line x1={w/2+p*2.8} y1={h*0.65} x2={w-f3-p*0.6} y2={h*0.65}
        stroke={color} strokeWidth="0.35" opacity="0.14" />
    </svg>
  );
}

function StyleDecoration({ style, color, w, h, p, designId }: {
  style: string; color: string; w: number; h: number; p: number; designId: string;
}) {
  switch (style) {
    case 'floral':      return <FloralDecoration      color={color} w={w} h={h} />;
    case 'elegant':     return <ElegantDecoration     color={color} w={w} h={h} p={p} />;
    case 'modern':      return <ModernDecoration      color={color} w={w} h={h} p={p} />;
    case 'sentimental': return <SentimentalDecoration color={color} w={w} h={h} p={p} />;
    case 'religious':   return <ReligiousDecoration   color={color} w={w} h={h} p={p} designId={designId} />;
    case 'luxury':      return <LuxuryDecoration      color={color} w={w} h={h} p={p} />;
    case 'kids':        return <KidsDecoration        color={color} w={w} h={h} p={p} />;
    default:            return null;
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

interface CardDesignPreviewProps {
  design:     DesignData;
  size?:      DesignPreviewSize;
  selected?:  boolean;
  showName?:  boolean;
  onClick?:   () => void;
  className?: string;
}

export default function CardDesignPreview({
  design,
  size      = 'md',
  selected  = false,
  showName  = true,
  onClick,
  className = '',
}: CardDesignPreviewProps) {
  const sz = SIZE_MAP[size];
  const { w, h, p } = sz;

  const bgStyle: CSSProperties =
    design.bg_type === 'gradient'
      ? { background: design.bg_color }
      : { backgroundColor: design.bg_color };

  const overlayStyle: CSSProperties =
    design.pattern !== 'none'
      ? { ...patternStyle(design.pattern, design.pattern_color), position: 'absolute', inset: 0 }
      : {};

  const heroText     = design.heading || design.name;
  const isDark       = design.text_color === '#FFFFFF' || design.text_color === '#fff';

  const paperOverlay: CSSProperties = !isDark ? {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='4'%3E%3Crect width='4' height='4' fill='none'/%3E%3Ccircle cx='1' cy='1' r='0.4' fill='rgba(0,0,0,0.025)'/%3E%3C/svg%3E")`,
  } : {};

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
      className={[
        size === 'xs' ? 'rounded-[4px]' : size === 'sm' ? 'rounded-[6px]' : size === 'md' ? 'rounded-[9px]' : 'rounded-[12px]',
        'relative overflow-hidden flex-shrink-0 flex flex-col',
        'transition-all duration-200',
        onClick ? 'cursor-pointer hover:scale-[1.04]' : '',
        className,
      ].join(' ')}
      style={{
        width:     w,
        height:    h,
        ...bgStyle,
        boxShadow: selected
          ? `0 0 0 2px ${design.accent_color}, 0 6px 24px rgba(0,0,0,0.20)`
          : '0 2px 14px rgba(0,0,0,0.14)',
      }}
    >
      {/* Paper texture for light cards */}
      {!isDark && (
        <div style={{ ...paperOverlay, position: 'absolute', inset: 0, pointerEvents: 'none' }} aria-hidden />
      )}

      {/* Pattern overlay */}
      {design.pattern !== 'none' && (
        <div style={overlayStyle} className="pointer-events-none" aria-hidden />
      )}

      {/* Style-specific decorative artwork */}
      <StyleDecoration
        style={design.style}
        color={design.accent_color}
        w={w} h={h} p={p}
        designId={design.id}
      />

      {/* ── Card face typography, centred for the landscape folded card ──────── */}
      <div
        className="relative z-10 flex flex-col h-full"
        style={{ padding: p }}
      >
        {/* Top rule */}
        <div style={{
          height:          sz.ruleW,
          backgroundColor: design.accent_color,
          opacity:         0.7,
          marginBottom:    p * 0.4,
          borderRadius:    1,
        }} />

        {/* Heading — centred in landscape */}
        <div style={{
          flex:           1,
          display:        'flex',
          flexDirection:  'column',
          justifyContent: 'center',
          alignItems:     'center',
          textAlign:      'center',
          gap:            size === 'xs' ? 1 : 2,
        }}>
          <p style={{
            fontFamily:    'Georgia, "Times New Roman", serif',
            fontSize:      sz.nameSize,
            fontWeight:    700,
            color:         design.text_color,
            lineHeight:    1.15,
            letterSpacing: '-0.02em',
            margin:        0,
            whiteSpace:    'nowrap',
          }}>
            {heroText}
          </p>

          {/* Short accent dash — centred */}
          <div style={{
            width:           p * 2.5,
            height:          sz.ruleW,
            backgroundColor: design.accent_color,
            opacity:         0.45,
            borderRadius:    1,
          }} />

          {/* Design name — small all-caps subtitle */}
          {showName && size !== 'xs' && (
            <p style={{
              fontFamily:    '"Inter", "Helvetica Neue", Arial, sans-serif',
              fontSize:      sz.subSize,
              color:         design.text_color,
              opacity:       0.4,
              letterSpacing: '0.13em',
              textTransform: 'uppercase' as const,
              margin:        0,
              lineHeight:    1.2,
            }}>
              {design.name}
            </p>
          )}
        </div>

        {/* Bottom row — ◆ diamond left · dots right */}
        <div style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          marginTop:      p * 0.3,
          marginBottom:   p * 0.3,
        }}>
          <svg width={sz.nameSize * 0.6} height={sz.nameSize * 0.6} viewBox="0 0 10 10" aria-hidden>
            <polygon points="5,0 10,5 5,10 0,5" fill={design.accent_color} opacity="0.55" />
          </svg>
          <div style={{ display: 'flex', gap: sz.ruleW * 2 }}>
            {[0, 1, 2].map((i) => (
              <div key={i} style={{
                width:           sz.ruleW * (i === 1 ? 2.5 : 1.5),
                height:          sz.ruleW * (i === 1 ? 2.5 : 1.5),
                borderRadius:    '50%',
                backgroundColor: design.accent_color,
                opacity:         i === 1 ? 0.6 : 0.3,
              }} />
            ))}
          </div>
        </div>

        {/* Bottom rule */}
        <div style={{
          height:          sz.ruleW,
          backgroundColor: design.accent_color,
          opacity:         0.7,
          borderRadius:    1,
        }} />
      </div>

      {/* Selected checkmark */}
      {selected && (
        <div
          className="absolute top-1.5 right-1.5 z-20 w-4 h-4 rounded-full flex items-center justify-center shadow"
          style={{ backgroundColor: design.accent_color }}
        >
          <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
    </div>
  );
}
